// html.js — tiny tagged-template helper that builds a DocumentFragment.
//
// Usage:
//   const frag = html`<div class="card"><h2>${title}</h2>${child}</div>`;
//   shadowRoot.append(frag);
//
// Substitutions are escaped by default for strings/numbers/booleans. Pass a
// Node, NodeList, or Array<Node> to insert real DOM. Pass a function to attach
// it as an event handler (use the `on:` prefix in attribute position).
//
// This is deliberately ~80 lines. It is not a templating engine; it is a
// convenience over raw DOM construction. If it breaks in 2035 you can replace
// it with `document.createElement` calls.

/**
 * @param {TemplateStringsArray} strings
 * @param {...unknown} values
 * @returns {DocumentFragment}
 */
export function html(strings, ...values) {
  const placeholders = [];
  let src = "";
  for (let i = 0; i < strings.length; i++) {
    src += strings[i];
    if (i < values.length) {
      const id = `__kfe_ph_${i}__`;
      placeholders.push({ id, value: values[i] });
      src += id;
    }
  }
  const tpl = document.createElement("template");
  tpl.innerHTML = src;
  const frag = tpl.content;

  // Walk and replace placeholders inside text nodes and attributes.
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  /** @type {Node | null} */ let node = walker.currentNode;
  const toFix = [];
  while ((node = walker.nextNode())) toFix.push(node);

  for (const n of toFix) {
    if (n.nodeType === Node.TEXT_NODE) replaceInText(/** @type {Text} */ (n), placeholders);
    else if (n.nodeType === Node.ELEMENT_NODE) replaceInElement(/** @type {Element} */ (n), placeholders);
  }
  return frag;
}

function replaceInText(textNode, placeholders) {
  let s = textNode.data;
  let changed = false;
  const parts = [];
  let cursor = 0;
  for (const ph of placeholders) {
    const idx = s.indexOf(ph.id, cursor);
    if (idx === -1) continue;
    if (idx > cursor) parts.push(document.createTextNode(s.slice(cursor, idx)));
    parts.push(toNode(ph.value));
    cursor = idx + ph.id.length;
    changed = true;
  }
  if (!changed) return;
  if (cursor < s.length) parts.push(document.createTextNode(s.slice(cursor)));
  const parent = textNode.parentNode;
  if (parent) {
    for (const p of parts) parent.insertBefore(p, textNode);
    parent.removeChild(textNode);
  }
}

function replaceInElement(el, placeholders) {
  for (const attr of [...el.attributes]) {
    let val = attr.value;
    let matched = false;
    for (const ph of placeholders) {
      if (val.includes(ph.id)) {
        matched = true;
        if (attr.name.startsWith("on:")) {
          const event = attr.name.slice(3);
          if (typeof ph.value === "function") {
            el.addEventListener(event, /** @type {EventListener} */ (ph.value));
          }
          el.removeAttribute(attr.name);
          break;
        }
        if (attr.name.startsWith(".")) {
          // .prop="${value}" — bind a property
          const prop = attr.name.slice(1);
          /** @type {any} */ (el)[prop] = ph.value;
          el.removeAttribute(attr.name);
          break;
        }
        val = val.replace(ph.id, String(ph.value ?? ""));
      }
    }
    if (matched && attr.name && el.hasAttribute(attr.name)) el.setAttribute(attr.name, val);
  }
}

/** @param {unknown} v @returns {Node} */
function toNode(v) {
  if (v instanceof Node) return v;
  if (Array.isArray(v) || v instanceof NodeList) {
    const f = document.createDocumentFragment();
    for (const child of /** @type {Iterable<unknown>} */ (v)) f.appendChild(toNode(child));
    return f;
  }
  return document.createTextNode(v == null ? "" : String(v));
}
